import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Orbit, Lock, KeyRound, Sparkles, ScrollText, CheckCircle2, Loader2,
  Trophy, Users, Link2, ScanText, Crown, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const fmtReq = (r) => {
  if (!r) return "";
  if (r.type === "decodes_min") return `Decode ${r.value}+ words`;
  if (r.type === "lessons_min") return `Complete ${r.value}+ lessons`;
  if (r.type === "level_min") return `Reach Level ${r.value}`;
  if (r.type === "type_min") return `Decode ${r.value}× ${r.decode_type}`;
  if (r.type === "words_all") return `Decode: ${(r.value || []).join(", ")}`;
  if (r.type === "words_any") return `Decode any of: ${(r.value || []).join(", ")}`;
  return "";
};

export default function Universe() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [activeRealm, setActiveRealm] = useState(null);
  const [realmData, setRealmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const loadOverview = async () => {
    try { const r = await api.get("/universe/overview"); setData(r.data); } catch (e) { /* ignore */ }
  };

  const runCheck = async (silent = false) => {
    setChecking(true);
    try {
      const r = await api.post("/universe/check", {});
      const nodes = r.data.newly_completed_nodes || [];
      const realms = r.data.newly_mastered_realms || [];
      if (!silent) {
        if (realms.length) realms.forEach((rm) => toast.success(`Realm mastered: ${rm.name}`));
        if (nodes.length) toast.success(`${nodes.length} node${nodes.length > 1 ? "s" : ""} lit up`);
        if (!nodes.length && !realms.length) toast("No new progress yet — keep decoding and walking the Path.");
      } else if (nodes.length || realms.length) {
        toast.success(`Universe updated: ${nodes.length} nodes, ${realms.length} realms mastered`);
      }
      await Promise.all([loadOverview(), refresh()]);
      if (activeRealm) openRealm(activeRealm, true);
    } catch (e) { /* ignore */ } finally { setChecking(false); }
  };

  const init = async () => {
    setLoading(true);
    await runCheck(true); // auto-grant any pending rewards on entry
    await loadOverview();
    setLoading(false);
  };
  useEffect(() => { init(); /* eslint-disable-next-line */ }, []);

  const openRealm = async (realmId, keepOpen = false) => {
    if (!keepOpen) { setActiveRealm(realmId); setRealmData(null); }
    try { const r = await api.get(`/universe/realm/${realmId}`); setRealmData(r.data); } catch (e) { toast.error("Could not open realm"); }
  };

  const gateHint = (realm) => {
    const g = realm.gate || {};
    const prev = data?.realms?.find((x) => x.order === realm.order - 1);
    const parts = [];
    if (prev) parts.push(`Master ${prev.name}`);
    if (g.level) parts.push(`reach Level ${g.level}`);
    if (g.decodes) parts.push(`${g.decodes} total decodes`);
    return parts.length ? `Locked — ${parts.join(" · ")}` : "Locked";
  };

  if (loading) return <div className="text-[var(--bone-faint)] text-sm" data-testid="universe-loading">Entering the Universe…</div>;
  if (!data) return <div className="text-[var(--bone-faint)] text-sm">Could not load the Universe.</div>;

  const t = data.totals || {};
  const season = data.season || {};
  const community = data.community || {};

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold" data-testid="universe-title">Universe Mode</h1>
          <p className="text-[var(--bone-muted)] text-sm" style={{ maxWidth: "64ch" }}>
            Ten realms of meaning. Unlock nodes by decoding, walking the Path, and completing word chains — then master each realm.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="green-text border-[rgba(20,90,67,0.5)]" data-testid="universe-progress-badge">
            {t.completed_nodes}/{t.nodes} nodes · {t.mastered_realms}/{t.realms} realms
          </Badge>
          <Button variant="outline" className="glass" onClick={() => runCheck(false)} disabled={checking} data-testid="universe-check-button">
            {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Sync progress
          </Button>
        </div>
      </div>

      {/* Season + collective event */}
      <div className="litra-card p-5 gold-edge" data-testid="universe-season">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[rgba(176,141,60,0.10)]"><Sparkles className="w-5 h-5 gold-text" /></div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display font-semibold gold-text">{season.name}</span>
                <span className="mono-chip text-[var(--bone-faint)]">{season.week}</span>
              </div>
              <p className="text-sm text-[var(--bone-muted)] mt-0.5" style={{ maxWidth: "60ch" }}>{season.blurb}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-xs text-[var(--bone-faint)] justify-end"><Users className="w-3.5 h-3.5" /> Community goal</div>
            <div className="font-display font-semibold gold-text" data-testid="universe-community-count">{community.count} / {community.goal}</div>
          </div>
        </div>
        <Progress value={community.pct || 0} className="h-1.5 mt-3" data-testid="universe-community-progress" />
      </div>

      {/* Realms grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.realms.map((realm) => {
          const pct = realm.node_total ? Math.round((realm.completed / realm.node_total) * 100) : 0;
          return (
            <button key={realm.id}
              onClick={() => realm.accessible ? openRealm(realm.id) : toast(gateHint(realm))}
              className={`litra-card p-5 text-left relative ${realm.accessible ? "litra-card-hover" : "opacity-70"} ${realm.mastered ? "gold-edge" : ""}`}
              data-testid={realm.accessible ? `universe-realm-${realm.id}` : `universe-realm-locked-${realm.order}`}>
              <div className="flex items-center justify-between">
                <span className="mono-chip text-[var(--bone-faint)]">REALM {String(realm.order).padStart(2, "0")}</span>
                {realm.mastered ? <Crown className="w-4 h-4 gold-text" /> : realm.accessible ? <Orbit className="w-4 h-4 green-text" /> : <Lock className="w-4 h-4 text-[var(--bone-faint)]" />}
              </div>
              <div className={`mt-2 font-display font-semibold ${realm.accessible ? "" : "text-[var(--bone-muted)]"}`}>{realm.name}</div>
              <p className="mt-1 text-sm text-[var(--bone-muted)]" style={{ minHeight: "2.5rem" }}>{realm.blurb}</p>
              {realm.accessible ? (
                <>
                  <Progress value={pct} className="h-1 mt-3" />
                  <div className="mt-1.5 flex items-center justify-between text-xs">
                    <span className="text-[var(--bone-faint)]">{realm.completed}/{realm.node_total} nodes</span>
                    {realm.mastered && <span className="gold-text flex items-center gap-1"><Trophy className="w-3 h-3" />Mastered</span>}
                  </div>
                </>
              ) : (
                <div className="mt-3 text-xs text-[var(--bone-faint)] flex items-start gap-1.5"><Lock className="w-3 h-3 mt-0.5" />{gateHint(realm)}</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Realm detail dialog */}
      <Dialog open={!!activeRealm} onOpenChange={(o) => { if (!o) { setActiveRealm(null); setRealmData(null); } }}>
        <DialogContent className="max-w-2xl bg-[#0F1115] border-white/[0.1] max-h-[85vh] overflow-auto scrollbar-thin">
          {!realmData ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span className="mono-chip green-text">REALM {String(realmData.order).padStart(2, "0")}</span>
                  {realmData.mastered && <span className="mono-chip gold-text flex items-center gap-1"><Crown className="w-3 h-3" />Mastered</span>}
                </div>
                <DialogTitle className="font-display text-xl">{realmData.name}</DialogTitle>
                <p className="text-sm text-[var(--bone-muted)]">{realmData.blurb}</p>
              </DialogHeader>

              <div className="space-y-2.5 mt-2">
                {realmData.nodes.map((n) => (
                  <div key={n.id} className={`rounded-xl border p-4 ${n.completed ? "border-[rgba(20,90,67,0.4)] bg-[rgba(20,90,67,0.06)]" : n.unlocked ? "border-white/[0.1] bg-white/[0.02]" : "border-white/[0.06] opacity-80"}`}
                    data-testid={`universe-node-${n.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        {n.completed ? <CheckCircle2 className="w-4 h-4 green-text" /> : n.unlocked ? <KeyRound className="w-4 h-4 gold-text" /> : <Lock className="w-4 h-4 text-[var(--bone-faint)]" />}
                        <span className="font-display font-semibold text-sm">{n.name}</span>
                      </div>
                      {n.path_node && <Badge variant="outline" className="text-[10px] border-[rgba(176,141,60,0.4)] gold-text">Path gate</Badge>}
                      {n.word_chain && <Badge variant="outline" className="text-[10px] border-[rgba(20,90,67,0.5)] green-text flex items-center gap-1"><Link2 className="w-2.5 h-2.5" />Chain</Badge>}
                    </div>
                    <p className="mt-1.5 text-sm text-[var(--bone-muted)]">{n.unlocked ? n.lore : n.blurb}</p>

                    {/* Locked requirement hints */}
                    {!n.unlocked && (
                      <div className="mt-2 space-y-1">
                        {n.path_node && <div className="text-xs text-[var(--bone-faint)] flex items-center gap-1.5"><Lock className="w-3 h-3" />Unlock by reaching this realm on the Path.</div>}
                        {(n.requirements || []).map((r, i) => <div key={i} className="text-xs text-[var(--bone-faint)] flex items-center gap-1.5"><Lock className="w-3 h-3" />{fmtReq(r)}</div>)}
                        {n.hint && !n.requirements?.length && !n.path_node && <div className="text-xs text-[var(--bone-faint)] flex items-center gap-1.5"><Lock className="w-3 h-3" />{n.hint}</div>}
                      </div>
                    )}

                    {/* Word chain progress */}
                    {n.word_chain && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-[var(--bone-faint)]">{n.word_chain.name}</span>
                          <span className={n.word_chain.complete ? "green-text" : "text-[var(--bone-muted)]"}>{n.word_chain.done}/{n.word_chain.total}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {n.word_chain.words.map((w) => {
                            const isDone = n.word_chain.complete || n.word_chain.words.slice(0, n.word_chain.done).includes(w);
                            return (
                              <span key={w} className={`mono-chip ${isDone ? "green-text border-[rgba(20,90,67,0.5)]" : "text-[var(--bone-faint)] border-white/[0.08]"} border rounded-full px-2.5 py-0.5`}>{isDone ? "✓ " : ""}{w}</span>
                            );
                          })}
                        </div>
                        {!n.word_chain.complete && n.word_chain.next && (
                          <Button size="sm" variant="outline" className="glass mt-2.5 h-7 text-xs" onClick={() => navigate("/decode", { state: { word: n.word_chain.next } })} data-testid={`universe-decode-${n.id}`}>
                            <ScanText className="w-3 h-3 mr-1" />Decode “{n.word_chain.next}”
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Artifacts */}
                    {n.unlocked && n.artifacts?.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {n.artifacts.map((a, i) => (
                          <div key={i} className="flex items-start gap-2 rounded-lg bg-white/[0.03] border border-white/[0.06] p-2 text-xs text-[var(--bone-muted)]">
                            <ScrollText className="w-3.5 h-3.5 gold-text mt-0.5 shrink-0" />{a}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {realmData.mastery_reward && (
                <div className="mt-2 rounded-xl border border-[rgba(176,141,60,0.35)] bg-[rgba(176,141,60,0.07)] p-3 text-xs text-[var(--bone-muted)] flex items-center gap-2">
                  <Trophy className="w-4 h-4 gold-text shrink-0" />
                  Master every node to earn this realm’s reward{realmData.mastery_reward.title ? `: the “${realmData.mastery_reward.title.name}” title` : realmData.mastery_reward.badge ? `: the “${realmData.mastery_reward.badge.name}” badge` : ""}.
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
